// Companies > detail > Payouts: the operator's payout account (Paystack plan
// PR 6). Masked: the backend sends the last four digits of the account
// number, never the number or the ID number. The audit trail lists every
// submission, approval, refused change (#542) and login-email change with the company phone at that
// moment; the one at the last approval is the number to call before approving
// a bank change (plan 6.10, PR 7b: PayoutReview asks for that call).
// Since PR 7a it also shows the bank check, the bank letter and the approval
// (PayoutReview) and the Paystack subaccounts (PayoutSubaccounts).

import React, { useCallback, useEffect, useState } from 'react';
import { Landmark } from 'lucide-react';
import { AdminCompanyAPI, APIUtils } from '../../services/api';
import PayoutReview from './PayoutReview';
import PayoutSubaccounts from './PayoutSubaccounts';
import { formatDateTime } from './payoutLabels';

const TYPE_LABEL = { business: 'Business', personal: 'Personal' };
const ACTION_LABEL = {
  submit: 'Payout account submitted',
  approve: 'Payout account approved',
  email_change: 'Login email changed',
  accept_letter: 'Bank letter accepted for the live subaccount',
  refuse: 'Payout account change refused',
};
// A bank change (#541): the save and the approval of one.
const CHANGE_LABEL = { submit: 'Payout account change submitted', approve: 'Payout account change approved' };
const labelOf = (entry) => (entry.change && CHANGE_LABEL[entry.action]) || ACTION_LABEL[entry.action] || entry.action;

const Field = ({ label, children }) => (
  <div>
    <dt className="text-gray-500">{label}</dt>
    <dd className="mt-0.5 font-medium text-gray-900">{children}</dd>
  </div>
);

const summaryOf = (entry) => {
  if (entry.action === 'email_change') return `From ${entry.email || 'unknown'} to ${entry.newEmail || 'unknown'}`;
  if (entry.action === 'refuse') return `Account ending ${entry.last4 || 'unknown'}. Note: ${entry.note || 'none'}`;
  if (entry.action === 'approve' && entry.calledPhone) return `Account ending ${entry.last4 || 'unknown'}, after a call to ${entry.calledPhone}`;
  if (entry.action === 'approve' || entry.action === 'accept_letter') return `Account ending ${entry.last4 || 'unknown'}`;
  return `Account ending ${entry.last4 || 'unknown'}, code sent to ${entry.email || 'unknown'}`;
};

const CompanyPayoutAccount = ({ companyId, isSuperAdmin = false }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => AdminCompanyAPI.getBankDetails(companyId)
    .then((res) => { setData(res.data.data); setError(null); })
    .catch((err) => setError(APIUtils.handleError(err).message || 'Could not load the payout account')), [companyId]);

  useEffect(() => {
    let cancelled = false;
    AdminCompanyAPI.getBankDetails(companyId)
      .then((res) => { if (!cancelled) setData(res.data.data); })
      .catch((err) => { if (!cancelled) setError(APIUtils.handleError(err).message || 'Could not load the payout account'); });
    return () => { cancelled = true; };
  }, [companyId]);

  // An action answers the new view; after a refusal, read it again.
  const onChanged = (next) => (next ? setData(next) : load());

  const account = data?.bankDetails;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Landmark size={18} className="text-emerald-600" />
        <h2 className="text-lg font-semibold text-gray-900">Payout account</h2>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {!data && !error && <p className="text-sm text-gray-500">Loading...</p>}

      {data && !account && <p className="text-sm text-gray-500">The operator has not submitted a payout account.</p>}

      {account && (
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <Field label="Bank">{account.bankName || 'Unknown'}{account.bankCode ? ` (${account.bankCode})` : ''}</Field>
          <Field label="Account type">{TYPE_LABEL[account.accountType] || 'Not given'}</Field>
          <Field label="Account number"><span className="font-mono">•••• {account.accountNumberLast4}</span></Field>
          <Field label="Account holder">{account.accountHolder || 'Not given'}</Field>
          {account.accountType === 'business' && <Field label="Registration number">{account.registrationNumber || 'Not given'}</Field>}
          {account.accountType === 'personal' && <Field label="ID number">{account.idNumberOnFile ? 'Fingerprint on file' : 'Not given'}</Field>}
          <Field label="Submitted">{account.submittedAt ? formatDateTime(account.submittedAt) : 'No date on record'}</Field>
        </dl>
      )}

      {account && data && (
        <>
          <PayoutReview companyId={companyId} data={data} isSuperAdmin={isSuperAdmin} onChanged={onChanged} />
          <PayoutSubaccounts companyId={companyId} data={data} isSuperAdmin={isSuperAdmin} onChanged={onChanged} />
        </>
      )}

      {data?.emailChangedAt && (
        <p className="mt-4 text-sm text-amber-700">Login email last changed {formatDateTime(data.emailChangedAt)}.</p>
      )}

      {data?.audit?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">History</h3>
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-md">
            {data.audit.map((entry, i) => (
              <li key={`${entry.at}-${i}`} className="px-3 py-2 text-sm flex flex-wrap justify-between gap-2">
                <span>
                  <span className="font-medium text-gray-900">{labelOf(entry)}</span>
                  <span className="text-gray-600">: {summaryOf(entry)}</span>
                </span>
                <span className="text-gray-500">
                  {formatDateTime(entry.at)}{entry.phone ? `, phone on file ${entry.phone}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CompanyPayoutAccount;
